import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  giftUserPackage,
  giftUserZToken,
  AdminUserDetail,
  AdminUserDeleteImpact,
  AdminUserSummary,
  AdminUserUpdatePayload,
  deleteAdminUser,
  deleteUserPackageAssignment,
  getAllRoles,
  getUserDeleteImpact,
  getUserDetail,
  resendUserEmailVerificationFromAdmin,
  updateAdminUser,
  updateUserBan,
} from "../api/adminUsersApi";
import {
  AdminCommentItem,
  deleteAdminComment,
  hideAdminComment,
  searchAdminComments,
  unhideAdminComment,
} from "../api/adminCommentsApi";
import { AdminSessionContext } from "../context/AdminSessionContext";
import { hasPermission } from "../utils/permissionHelpers";
import { confirmDialog, notifyDialog, promptReasonDialog } from "../utils/dialogs";
import useAdminUsers, { type AdminUsersSortOption } from "../hooks/useAdminUsers";
import { getAdminPackages, type AdminPackageItem } from "../api/adminEconomyApi";

const PAGE_SIZE = 20;
const COMMENT_PAGE_SIZE = 10;

type SortOption = AdminUsersSortOption;

const SORT_LABELS: Record<SortOption, string> = {
  nameAsc: "Isme gore (A-Z)",
  nameDesc: "Isme gore (Z-A)",
  zAsc: "Z-Token artan",
  zDesc: "Z-Token azalan",
};

type StatusFilter = "all" | "active" | "platformBan" | "commentBan";

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: "Tum durumlar",
  active: "Sadece aktif",
  platformBan: "Platform yasakli",
  commentBan: "Yorum yasakli",
};

type SegmentKey = "all" | "staff" | "banned" | "comment" | "highBalance";

interface SegmentOption {
  key: SegmentKey;
  label: string;
  helper: string;
  predicate: (user: AdminUserSummary) => boolean;
}

const SEGMENT_OPTIONS: SegmentOption[] = [
  {
    key: "all",
    label: "Tum kullanicilar",
    helper: "Genel liste",
    predicate: () => true,
  },
  {
    key: "staff",
    label: "Yonetim / Mod",
    helper: "Rolunde admin veya mod gecenler",
    predicate: (user) => {
      const roles =
        user.roles && user.roles.length > 0
          ? user.roles
          : user.role
            ? [user.role]
            : [];
      return roles
        .map((role) => role.toLowerCase())
        .some((role) => role.includes("admin") || role.includes("mod"));
    },
  },
  {
    key: "banned",
    label: "Platform yasakli",
    helper: "Tum platform banlari",
    predicate: (user) => user.isBannedAll,
  },
  {
    key: "comment",
    label: "Yorum yasakli",
    helper: "Yorum banli kullanicilar",
    predicate: (user) => user.isCommentBanned,
  },
  {
    key: "highBalance",
    label: "1000+ Z bakiyesi",
    helper: "Cuzdani yuksek olanlar",
    predicate: (user) => user.zTokenBalance >= 1000,
  },
];

interface Filters {
  search: string;
  role: string;
  sort: AdminUsersSortOption;
}

const DEFAULT_FILTERS: Filters = {
  search: "",
  role: "",
  sort: "nameAsc",
};

const DEFAULT_AVATAR_TOKEN = "default-avatar";

function hasCustomAvatar(url?: string | null): boolean {
  if (!url) {
    return false;
  }
  const normalized = url.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return !normalized.includes(DEFAULT_AVATAR_TOKEN);
}

function getInitials(value?: string | null, fallback = "?"): string {
  if (!value) {
    return fallback;
  }
  const words = value
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) {
    return value.slice(0, 2).toUpperCase() || fallback;
  }
  const letters = words.map((word) => word[0]);
  return letters.join("").slice(0, 2).toUpperCase();
}

interface EditFormState {
  name: string;
  email: string;
  isEmailVerified: boolean;
  phone: string;
  roles: string[];
  zTokenBalance: string;
  nameColor: string;
  avatarFrame: string;
  avatarUrl: string;
  backgroundUrl: string;
  creatorDailyPartLimit: string;
  excludeFromViewMetrics: boolean;
}

type FinanceTimelineFilter = "all" | "transaction" | "purchase" | "unlock";

type UserFinanceTimelineItem = {
  id: string;
  kind: Exclude<FinanceTimelineFilter, "all">;
  title: string;
  occurredAt: string;
  amountZ?: number | null;
  amountFiat?: string | null;
  status?: string | null;
  source?: string | null;
  reason?: string | null;
};

export default function AdminUsersModeration() {
  const { session } = useContext(AdminSessionContext);
  const permissions = session?.permissions;
  const canViewBalances = hasPermission(permissions, "Users.ViewBalances");
  const canEditProfile = hasPermission(permissions, "Users.EditProfile");
  const canAdjustZToken = hasPermission(permissions, "Users.AdjustZToken");
  const canAssignRole = hasPermission(permissions, "Users.AssignRole");
  const canBanAll = hasPermission(permissions, "Users.BanAll");
  const canBanComments = hasPermission(permissions, "Users.BanComments");
  const canViewProgress = hasPermission(permissions, "Users.ViewProgress");
  const canViewComments = hasPermission(permissions, "Comments.View");
  const canModerateComments = hasPermission(permissions, "Comments.Moderate");
  const canDeleteComments = hasPermission(permissions, "Comments.Delete");
  const canSeeSensitive = hasPermission(permissions, "Users.ViewSensitive");
  const canCriticalUserActions = useMemo(
    () => (session?.roles ?? []).some((role) => role.toLowerCase() === "admin"),
    [session?.roles],
  );
  const canGiftActions = useMemo(
    () => (session?.roles ?? []).some((role) => role.toLowerCase() === "admin"),
    [session?.roles],
  );

  const location = useLocation();
  const navigate = useNavigate();
  const [searchDraft, setSearchDraft] = useState("");
  const [filters, setFilters] = useState<Filters>({ ...DEFAULT_FILTERS });
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [minBalanceFilter, setMinBalanceFilter] = useState("");
  const [minLevelFilter, setMinLevelFilter] = useState("");
  const [segment, setSegment] = useState<SegmentKey>("all");

  const userQuery = useAdminUsers({
    search: filters.search || undefined,
    role: filters.role || undefined,
    sort: filters.sort,
    page,
    pageSize: PAGE_SIZE,
  });
  const userItems = userQuery.data?.items;
  const users = useMemo(() => userItems ?? [], [userItems]);
  const totalCount = userQuery.data?.totalCount ?? 0;
  const loadingList = userQuery.isLoading && !userQuery.data;
  const listError = userQuery.error ? "Kullanici listesi yuklenemedi." : null;

  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [packageOptions, setPackageOptions] = useState<AdminPackageItem[]>([]);
  const [giftLoading, setGiftLoading] = useState<"ztoken" | "package" | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pendingFocusUser, setPendingFocusUser] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [emailResendLoading, setEmailResendLoading] = useState(false);
  const [emailResendMessage, setEmailResendMessage] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [packageDeleteId, setPackageDeleteId] = useState<string | null>(null);

  const [showComments, setShowComments] = useState(false);
  const [commentPage, setCommentPage] = useState(0);
  const [comments, setComments] = useState<AdminCommentItem[]>([]);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentActionId, setCommentActionId] = useState<string | null>(null);

  const loadComments = (pageIndex: number) => {
    if (!selectedUserId) {
      return;
    }
    setCommentLoading(true);
    setCommentError(null);

    searchAdminComments({
      userId: selectedUserId,
      skip: pageIndex * COMMENT_PAGE_SIZE,
      take: COMMENT_PAGE_SIZE,
    })
      .then((res) => {
        setComments(res.items);
        setCommentTotal(res.totalCount);
      })
      .catch((error) => {
        console.error("searchAdminComments error", error);
        setCommentError("Yorumlar yuklenemedi.");
        setComments([]);
        setCommentTotal(0);
      })
      .finally(() => setCommentLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    getAllRoles().then((names) => {
      if (!cancelled) {
        setRoleOptions(names);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canGiftActions) {
      setPackageOptions([]);
      return;
    }

    let active = true;
    getAdminPackages()
      .then((packages) => {
        if (active) {
          setPackageOptions(packages);
        }
      })
      .catch(() => {
        if (active) {
          setPackageOptions([]);
        }
      });

    return () => {
      active = false;
    };
  }, [canGiftActions]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focus = params.get("focus");
    if (focus) {
      setPendingFocusUser(focus);
    }
  }, [location.search]);

  useEffect(() => {
    const trimmed = searchDraft.trim();
    const timer = setTimeout(() => {
      setFilters((prev) => (prev.search === trimmed ? prev : { ...prev, search: trimmed }));
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchDraft]);

  useEffect(() => {
    if (filters.search !== searchDraft) {
      setSearchDraft(filters.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  useEffect(() => {
    if (!pendingFocusUser || users.length === 0) {
      return;
    }
    const match = users.find((user) => user.id === pendingFocusUser);
    if (match) {
      setSelectedUserId(match.id);
      setPendingFocusUser(null);
      const params = new URLSearchParams(location.search);
      params.delete("focus");
      navigate({ pathname: "/admin/users", search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
    }
  }, [pendingFocusUser, users, navigate, location.search]);

  useEffect(() => {
    if (!userQuery.data) {
      if (!userQuery.isLoading) {
        setSelectedUserId(null);
        setDetail(null);
        setDetailError(null);
      }
      return;
    }

    const items = userQuery.data.items;
    if (items.length === 0) {
      setSelectedUserId(null);
      setDetail(null);
      setDetailError(null);
      return;
    }

    setSelectedUserId((current) => {
      if (current && items.some((item) => item.id === current)) {
        return current;
      }
      return items[0].id;
    });
  }, [userQuery.data, userQuery.isLoading]);

  useEffect(() => {
    if (!selectedUserId) {
      setDetail(null);
      setDetailError(null);
      setEditMode(false);
      setEditForm(null);
      setEmailResendMessage(null);
      setEmailResendLoading(false);
      setShowComments(false);
      return;
    }

    let active = true;
    setDetailLoading(true);
    setDetailError(null);
    setEmailResendMessage(null);
    setEmailResendLoading(false);

    getUserDetail(selectedUserId)
      .then((data) => {
        if (!active) {
          return;
        }
        if (!data) {
          setDetail(null);
          setDetailError("Kullanici detayina erisilemedi.");
          setEditMode(false);
          setEditForm(null);
          return;
        }
        setDetail(data);
        setEditForm(buildEditForm(data));
      })
      .catch((error) => {
        console.error("getUserDetail error", error);
        if (active) {
          setDetail(null);
          setDetailError("Kullanici detayina erisilemedi.");
          setEditMode(false);
          setEditForm(null);
        }
      })
      .finally(() => {
        if (active) {
          setDetailLoading(false);
          setShowComments(false);
          setComments([]);
          setCommentTotal(0);
          setCommentPage(0);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedUserId]);

  useEffect(() => {
    if (!showComments || !selectedUserId) {
      return;
    }
    loadComments(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showComments, selectedUserId]);

  useEffect(() => {
    if (!showComments || !selectedUserId) {
      return;
    }
    loadComments(commentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentPage]);

  const totalPages = useMemo(() => {
    if (totalCount === 0) {
      return 0;
    }
    return Math.ceil(totalCount / PAGE_SIZE);
  }, [totalCount]);

  const commentTotalPages = useMemo(() => {
    if (commentTotal === 0) {
      return 0;
    }
    return Math.ceil(commentTotal / COMMENT_PAGE_SIZE);
  }, [commentTotal]);

  const combinedRoles = useMemo(() => {
    const roleSet = new Set<string>();
    roleOptions.forEach((role) => {
      const trimmed = role.trim();
      if (trimmed) {
        roleSet.add(trimmed);
      }
    });
    users.forEach((user) => {
      const source = user.roles && user.roles.length > 0 ? user.roles : user.role ? [user.role] : [];
      source.forEach((role) => {
        const trimmed = role.trim();
        if (trimmed) {
          roleSet.add(trimmed);
        }
      });
    });
    const detailRoles = detail?.roles && detail.roles.length > 0 ? detail.roles : detail?.role ? [detail.role] : [];
    detailRoles.forEach((role) => {
      const trimmed = role.trim();
      if (trimmed) {
        roleSet.add(trimmed);
      }
    });
    return Array.from(roleSet).sort((a, b) => a.localeCompare(b));
  }, [roleOptions, users, detail?.roles, detail?.role]);

  const segmentCounts = useMemo(() => {
    const base = SEGMENT_OPTIONS.reduce((acc, option) => {
      acc[option.key] = 0;
      return acc;
    }, {} as Record<SegmentKey, number>);
    users.forEach((user) => {
      SEGMENT_OPTIONS.forEach((option) => {
        if (option.predicate(user)) {
          base[option.key] += 1;
        }
      });
    });
    return base;
  }, [users]);

  const activeSegment = useMemo(
    () => SEGMENT_OPTIONS.find((option) => option.key === segment) ?? SEGMENT_OPTIONS[0],
    [segment],
  );

  const minBalanceValue = useMemo(() => parseFilterValue(minBalanceFilter), [minBalanceFilter]);
  const minLevelValue = useMemo(() => parseFilterValue(minLevelFilter), [minLevelFilter]);

  const visibleUsers = useMemo(() => {
    return users.filter((user) => {
      if (!activeSegment.predicate(user)) {
        return false;
      }
      if (statusFilter === "active" && (user.isBannedAll || user.isCommentBanned)) {
        return false;
      }
      if (statusFilter === "platformBan" && !user.isBannedAll) {
        return false;
      }
      if (statusFilter === "commentBan" && !user.isCommentBanned) {
        return false;
      }
      if (minBalanceValue !== null && user.zTokenBalance < minBalanceValue) {
        return false;
      }
      if (minLevelValue !== null) {
        const level = user.level ?? 0;
        if (level < minLevelValue) {
          return false;
        }
      }
      return true;
    });
  }, [users, activeSegment, statusFilter, minBalanceValue, minLevelValue]);

  const clientFilterActive =
    segment !== "all" ||
    statusFilter !== "all" ||
    minBalanceFilter.trim().length > 0 ||
    minLevelFilter.trim().length > 0;

  useEffect(() => {
    if (visibleUsers.length === 0) {
      if (selectedUserId !== null) {
        setSelectedUserId(null);
      }
      return;
    }
    if (!selectedUserId || !visibleUsers.some((user) => user.id === selectedUserId)) {
      setSelectedUserId(visibleUsers[0].id);
    }
  }, [visibleUsers, selectedUserId]);

  const handleRoleChange = (role: string) => {
    setFilters((prev) => ({ ...prev, role }));
    setPage(0);
  };

  const handleSortChange = (sort: SortOption) => {
    setFilters((prev) => ({ ...prev, sort }));
    setPage(0);
  };

  const handleStatusFilterChange = (status: StatusFilter) => {
    setStatusFilter(status);
  };

  const handleMinBalanceChange = (value: string) => {
    setMinBalanceFilter(value);
  };

  const handleMinLevelChange = (value: string) => {
    setMinLevelFilter(value);
  };

  const handleSegmentChange = (key: SegmentKey) => {
    setSegment((prev) => (prev === key ? prev : key));
  };

  const handleResetFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
    setSearchDraft("");
    setStatusFilter("all");
    setMinBalanceFilter("");
    setMinLevelFilter("");
    setSegment("all");
    setPage(0);
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 0 || (totalPages > 0 && nextPage >= totalPages)) {
      return;
    }
    setPage(nextPage);
  };

  const handleCommentPageChange = (nextPage: number) => {
    if (nextPage < 0 || (commentTotalPages > 0 && nextPage >= commentTotalPages)) {
      return;
    }
    setCommentPage(nextPage);
  };

  const startEdit = () => {
    if (!detail) {
      return;
    }
    setEditForm(buildEditForm(detail));
    setEditMode(true);
    setSaveError(null);
  };

  const cancelEdit = () => {
    if (detail) {
      setEditForm(buildEditForm(detail));
    } else {
      setEditForm(null);
    }
    setEditMode(false);
    setSaveError(null);
  };

  const handleEditChange = (key: keyof EditFormState, value: string | boolean) => {
    setEditForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleRoleToggle = (roleName: string, checked: boolean) => {
    const normalized = roleName.trim();
    if (!normalized) {
      return;
    }
    setEditForm((prev) => {
      if (!prev) {
        return prev;
      }
      const current = new Set(prev.roles ?? []);
      if (checked) {
        current.add(normalized);
      } else {
        if (current.size <= 1 && current.has(normalized)) {
          return prev;
        }
        current.delete(normalized);
      }
      return { ...prev, roles: Array.from(current) };
    });
  };

  const handleSave = async () => {
    if (!selectedUserId || !detail || !editForm || !canEditProfile) {
      return;
    }

    setSaveLoading(true);
    setSaveError(null);

    const sanitizedRoles = (editForm.roles ?? []).filter((role) => role.trim().length > 0);
    if (canAssignRole && sanitizedRoles.length === 0) {
      setSaveError("En az bir rol seçmelisiniz.");
      setSaveLoading(false);
      return;
    }

    const payload: AdminUserUpdatePayload = {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      isEmailVerified: editForm.isEmailVerified,
      phone: canSeeSensitive ? editForm.phone.trim() : undefined,
      roleName: canAssignRole ? (sanitizedRoles[0] ?? detail.role ?? "?ye") : undefined,
      roleNames: canAssignRole ? sanitizedRoles : undefined,
      zTokenBalance: canAdjustZToken ? parseNumber(editForm.zTokenBalance, detail.zTokenBalance) : undefined,
      nameColor: editForm.nameColor.trim(),
      avatarFrame: editForm.avatarFrame.trim() || null,
      avatarUrl: editForm.avatarUrl.trim(),
      backgroundUrl: editForm.backgroundUrl.trim(),
      creatorDailyPartLimit: parseOptionalPositiveInt(editForm.creatorDailyPartLimit),
      excludeFromViewMetrics: editForm.excludeFromViewMetrics,
    };

    const updated = await updateAdminUser(selectedUserId, payload);
    if (!updated) {
      setSaveError("Kullanici bilgileri guncellenemedi.");
      setSaveLoading(false);
      return;
    }

    setDetail(updated);
    setEditForm(buildEditForm(updated));
    setEditMode(false);
    setSaveLoading(false);

    userQuery.mutate(
      (current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          items: current.items.map((user) =>
            user.id === updated.id
              ? {
                  ...user,
                  username: updated.username,
                  email: updated.email,
                  isEmailVerified: updated.isEmailVerified ?? user.isEmailVerified,
                  role: updated.role,
                  roles: updated.roles,
                  zTokenBalance: updated.zTokenBalance,
                  nameColor: updated.nameColor,
                  avatarFrame: updated.avatarFrame ?? undefined,
                  avatarUrl: updated.avatarUrl ?? undefined,
                  backgroundUrl: updated.backgroundUrl ?? undefined,
                  phone: updated.phone ?? undefined,
                  level: updated.progress?.level ?? user.level,
                  creatorDailyPartLimit: updated.creatorDailyPartLimit ?? user.creatorDailyPartLimit,
                  excludeFromViewMetrics: updated.excludeFromViewMetrics ?? user.excludeFromViewMetrics,
                }
              : user,
          ),
        };
      },
      false,
    );
  };

  const handleBanUpdate = async (field: "isBannedAll" | "isCommentBanned", value: boolean) => {
    if (!detail) {
      return;
    }
    const success = await updateUserBan(detail.id, {
      isBannedAll: field === "isBannedAll" ? value : undefined,
      isCommentBanned: field === "isCommentBanned" ? value : undefined,
    });
    if (!success) {
      return;
    }
    setDetail((prev) => (prev ? { ...prev, [field]: value } : prev));
    userQuery.mutate(
      (current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          items: current.items.map((user) =>
            user.id === detail.id
              ? {
                  ...user,
                  isBannedAll: field === "isBannedAll" ? value : user.isBannedAll,
                  isCommentBanned: field === "isCommentBanned" ? value : user.isCommentBanned,
                }
              : user,
          ),
        };
      },
      false,
    );
  };

  const handleAdminResendVerification = async () => {
    if (!detail || emailResendLoading) {
      return;
    }

    setEmailResendLoading(true);
    setEmailResendMessage(null);
    const result = await resendUserEmailVerificationFromAdmin(detail.id);
    setEmailResendMessage(result.message);
    setEmailResendLoading(false);
  };

  const handleDeleteUser = async (mode: "soft" | "hard") => {
    if (!detail || deleteLoading) {
      return;
    }

    let impact: AdminUserDeleteImpact | null = null;
    if (mode === "hard") {
      impact = await getUserDeleteImpact(detail.id);
      const warningLines = impact
        ? [
            `Toplam etkilenebilecek kayıt: ${formatNumber(impact.total)}`,
            `Seri: ${formatNumber(impact.contents)}`,
            `Yorum: ${formatNumber(impact.comments)}`,
            `Paket: ${formatNumber(impact.packages)}`,
            `Satın alma: ${formatNumber(impact.purchases)}`,
          ]
        : ["Bağımlılık özeti alınamadı."];
      const hardConfirm = await confirmDialog({
        title: "Kullanıcıyı kalıcı sil",
        text: `HARD DELETE geri alınamaz. ${warningLines.join(" ")} Devam etmek istiyor musunuz?`,
        confirmText: "Evet, sil",
        danger: true,
      });
      if (!hardConfirm) {
        return;
      }
    } else {
      const softConfirm = await confirmDialog({
        title: "Kullanıcıyı devre dışı bırak",
        text: "Kullanıcı soft-delete yapılacak. Hesap erişimi kapatılır ve liste dışına alınır. Devam edilsin mi?",
        confirmText: "Evet, sil",
        danger: true,
      });
      if (!softConfirm) {
        return;
      }
    }

    const reasonInput = await promptReasonDialog({
      title: "Silme nedeni",
      label: "Gerekçe (zorunlu, en az 5 karakter)",
      minLength: 5,
      danger: true,
    });
    if (reasonInput === null) {
      return;
    }
    const reason = reasonInput.trim();
    if (reason.length < 5) {
      await notifyDialog("Silme işlemi için en az 5 karakterlik bir neden girmelisiniz.", "error");
      return;
    }

    setDeleteLoading(true);
    const result = await deleteAdminUser(detail.id, {
      hardDelete: mode === "hard",
      confirmHardDelete: mode === "hard",
      reason,
    });
    setDeleteLoading(false);

    if (!result.success) {
      const details = result.impact
        ? `\nToplam: ${formatNumber(result.impact.total)} | Seri: ${formatNumber(result.impact.contents)} | Paket: ${formatNumber(result.impact.packages)}`
        : "";
      await notifyDialog((result.message ?? "Kullanıcı silinemedi.") + details, "error");
      return;
    }

    await userQuery.mutate();
    setDetail(null);
    setSelectedUserId(null);
    setEditMode(false);
    setEditForm(null);
    setShowComments(false);
    await notifyDialog(mode === "hard" ? "Kullanıcı hard-delete ile kalıcı silindi." : "Kullanıcı soft-delete ile devre dışı bırakıldı.", "success");
  };

  const handleDeleteUserPackage = async (userPackageId: string, mode: "soft" | "hard") => {
    if (!detail || packageDeleteId) {
      return;
    }

    const confirmed = await confirmDialog({
      title: "Paket kaydını sil",
      text:
        mode === "hard"
          ? "Paket kaydı HARD DELETE ile kalıcı silinecek. Bu işlem geri alınamaz. Devam edilsin mi?"
          : "Paket kaydı soft-delete edilerek anında pasife alınacak. Devam edilsin mi?",
      confirmText: "Evet, sil",
      danger: true,
    });
    if (!confirmed) {
      return;
    }

    const reasonInput = await promptReasonDialog({
      title: "Paket silme nedeni",
      label: "Gerekçe (zorunlu, en az 5 karakter)",
      minLength: 5,
      danger: true,
    });
    if (reasonInput === null) {
      return;
    }
    const reason = reasonInput.trim();
    if (reason.length < 5) {
      await notifyDialog("Paket silme işlemi için en az 5 karakterlik bir neden girmelisiniz.", "error");
      return;
    }

    setPackageDeleteId(userPackageId);
    const result = await deleteUserPackageAssignment(detail.id, userPackageId, {
      hardDelete: mode === "hard",
      confirmHardDelete: mode === "hard",
      reason,
    });
    setPackageDeleteId(null);

    if (!result.success) {
      const suffix = typeof result.linkedReadLogs === "number" ? `\nBağlı okuma kaydı: ${formatNumber(result.linkedReadLogs)}` : "";
      await notifyDialog((result.message ?? "Paket kaydı silinemedi.") + suffix, "error");
      return;
    }

    const refreshed = await getUserDetail(detail.id);
    if (refreshed) {
      setDetail(refreshed);
      setEditForm(buildEditForm(refreshed));
    }
    await notifyDialog(mode === "hard" ? "Paket kaydı kalıcı silindi." : "Paket kaydı pasife alındı.", "success");
  };

  const handleGiftZToken = async (amount: number, note?: string): Promise<boolean> => {
    if (!detail || !canGiftActions || amount <= 0 || giftLoading) {
      return false;
    }

    setGiftLoading("ztoken");
    const success = await giftUserZToken(detail.id, {
      amount,
      note,
    });
    setGiftLoading(null);

    if (!success) {
      await notifyDialog("Z-Token hediyesi gönderilemedi.", "error");
      return false;
    }

    const refreshed = await getUserDetail(detail.id);
    if (refreshed) {
      setDetail(refreshed);
      setEditForm(buildEditForm(refreshed));
    }
    await userQuery.mutate();
    await notifyDialog("Z-Token hediyesi başarıyla gönderildi.", "success");
    return true;
  };

  const handleGiftPackage = async (packageId: string, multiplier: number, note?: string): Promise<boolean> => {
    if (!detail || !canGiftActions || !packageId || multiplier <= 0 || giftLoading) {
      return false;
    }

    setGiftLoading("package");
    const success = await giftUserPackage(detail.id, {
      packageId,
      durationMultiplier: Math.max(1, Math.trunc(multiplier)),
      note,
    });
    setGiftLoading(null);

    if (!success) {
      await notifyDialog("Paket hediyesi gönderilemedi.", "error");
      return false;
    }

    const refreshed = await getUserDetail(detail.id);
    if (refreshed) {
      setDetail(refreshed);
      setEditForm(buildEditForm(refreshed));
    }
    await userQuery.mutate();
    await notifyDialog("Paket hediyesi başarıyla tanımlandı.", "success");
    return true;
  };

  const toggleComments = () => {
    if (!detail || !canViewComments) {
      return;
    }
    const next = !showComments;
    setShowComments(next);
    if (!next) {
      setComments([]);
      setCommentTotal(0);
      setCommentPage(0);
      setCommentError(null);
      return;
    }
    setCommentPage(0);
  };

  const handleCommentAction = async (action: "hide" | "unhide" | "delete", commentId: string) => {
    if (!canViewComments) {
      return;
    }
    if ((action === "hide" || action === "unhide") && !canModerateComments) {
      return;
    }
    if (action === "delete" && !canDeleteComments) {
      return;
    }
    setCommentActionId(commentId);
    let success = false;
    if (action === "hide") success = await hideAdminComment(commentId);
    if (action === "unhide") success = await unhideAdminComment(commentId);
    if (action === "delete") success = await deleteAdminComment(commentId);
    if (success) {
      loadComments(commentPage);
    }
    setCommentActionId(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <SegmentSelector selectedKey={segment} counts={segmentCounts} onSelect={handleSegmentChange} />

      <FilterBar
        searchValue={searchDraft}
        onSearchChange={setSearchDraft}
        selectedRole={filters.role}
        onRoleChange={handleRoleChange}
        selectedSort={filters.sort}
        onSortChange={handleSortChange}
        roleOptions={combinedRoles}
        totalCount={totalCount}
        sortLabelsMap={SORT_LABELS}
        statusValue={statusFilter}
        onStatusChange={handleStatusFilterChange}
        minBalanceValue={minBalanceFilter}
        onMinBalanceChange={handleMinBalanceChange}
        minLevelValue={minLevelFilter}
        onMinLevelChange={handleMinLevelChange}
        onResetFilters={handleResetFilters}
        clientFilterActive={clientFilterActive}
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_3fr]">
        <UserListPanel
          users={visibleUsers}
          loading={loadingList}
          error={listError}
          selectedUserId={selectedUserId}
          onSelectUser={setSelectedUserId}
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalCount={totalCount}
          clientFilterActive={clientFilterActive}
          segmentLabel={activeSegment.label}
          canViewBalances={canViewBalances}
        />

        <UserDetailPanel
          detail={detail}
          loading={detailLoading}
          error={detailError}
          editMode={editMode}
          onStartEdit={startEdit}
          onCancelEdit={cancelEdit}
          editForm={editForm}
          onEditChange={handleEditChange}
          onRoleToggle={handleRoleToggle}
          onSave={handleSave}
          saveLoading={saveLoading}
          saveError={saveError}
          deleteLoading={deleteLoading}
          onDeleteUser={handleDeleteUser}
          onDeleteUserPackage={handleDeleteUserPackage}
          packageDeleteId={packageDeleteId}
          roleOptions={combinedRoles}
          onBanToggle={handleBanUpdate}
          showComments={showComments}
          onToggleComments={toggleComments}
          comments={comments}
          commentLoading={commentLoading}
          commentError={commentError}
          commentPage={commentPage}
          commentTotalPages={commentTotalPages}
          onCommentPageChange={handleCommentPageChange}
          onCommentAction={handleCommentAction}
          commentActionId={commentActionId}
          canViewBalances={canViewBalances}
          canViewComments={canViewComments}
          canModerateComments={canModerateComments}
          canDeleteComments={canDeleteComments}
          canEditProfile={canEditProfile}
          canBanAll={canBanAll}
          canBanComments={canBanComments}
          canSeeSensitive={canSeeSensitive}
          canAdjustZToken={canAdjustZToken}
          canAssignRole={canAssignRole}
          canViewProgress={canViewProgress}
          canCriticalUserActions={canCriticalUserActions}
          canGiftActions={canGiftActions}
          packageOptions={packageOptions}
          giftLoading={giftLoading}
          onResendEmailVerification={handleAdminResendVerification}
          emailResendLoading={emailResendLoading}
          emailResendMessage={emailResendMessage}
          onGiftZToken={handleGiftZToken}
          onGiftPackage={handleGiftPackage}
        />
      </div>
    </div>
  );
}

function SegmentSelector({
  selectedKey,
  counts,
  onSelect,
}: {
  selectedKey: SegmentKey;
  counts: Record<SegmentKey, number>;
  onSelect: (key: SegmentKey) => void;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap gap-2 p-3">
        {SEGMENT_OPTIONS.map((option) => {
          const active = option.key === selectedKey;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onSelect(option.key)}
              className={`flex flex-1 min-w-[160px] flex-col gap-1 rounded-lg border px-3 py-2 text-left transition ${
                active ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm" : "border-slate-200 bg-white text-slate-700"
              }`}
            >
              <span className="text-xs font-semibold uppercase">{option.label}</span>
              <span className="text-[11px] text-slate-500">{option.helper}</span>
              <span className="text-sm font-semibold text-slate-900">{formatNumber(counts[option.key] ?? 0)}</span>
            </button>
          );
        })}
      </div>
      <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500">
        Segment filtreleri yalnizca aktif sayfada listelenen kullanicilara uygulanir.
      </div>
    </div>
  );
}

function FilterBar({
  searchValue,
  onSearchChange,
  selectedRole,
  onRoleChange,
  selectedSort,
  onSortChange,
  roleOptions,
  totalCount,
  sortLabelsMap,
  statusValue,
  onStatusChange,
  minBalanceValue,
  onMinBalanceChange,
  minLevelValue,
  onMinLevelChange,
  onResetFilters,
  clientFilterActive,
}: {
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedRole: string;
  onRoleChange: (value: string) => void;
  selectedSort: SortOption;
  onSortChange: (value: SortOption) => void;
  roleOptions: string[];
  totalCount: number;
  sortLabelsMap: Record<SortOption, string>;
  statusValue: StatusFilter;
  onStatusChange: (value: StatusFilter) => void;
  minBalanceValue: string;
  onMinBalanceChange: (value: string) => void;
  minLevelValue: string;
  onMinLevelChange: (value: string) => void;
  onResetFilters: () => void;
  clientFilterActive: boolean;
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-1 flex-col gap-2 lg:flex-row">
        <div className="flex flex-1 flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500" htmlFor="admin-user-search">
            Kullanici arayin
          </label>
          <input
            id="admin-user-search"
            type="text"
            placeholder="Isim veya e-posta ile ara..."
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1 lg:w-48">
          <label className="text-xs font-semibold text-gray-500" htmlFor="admin-user-role">
            Rol filtresi
          </label>
          <select
            id="admin-user-role"
            value={selectedRole}
            onChange={(event) => onRoleChange(event.target.value)}
            className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
          >
            <option value="">Tum roller</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1 lg:w-56">
          <label className="text-xs font-semibold text-gray-500" htmlFor="admin-user-sort">
            Siralama
          </label>
          <select
            id="admin-user-sort"
            value={selectedSort}
            onChange={(event) => onSortChange(event.target.value as SortOption)}
            className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
          >
            {(Object.keys(sortLabelsMap) as SortOption[]).map((key) => (
              <option key={key} value={key}>
                {sortLabelsMap[key]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
        <span>
          Toplam <span className="font-semibold text-gray-700">{formatNumber(totalCount)}</span> kullanici
          {clientFilterActive && <span className="ml-2 text-blue-600">• Gelismis filtreler aktif</span>}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setAdvancedOpen((prev) => !prev)}
            className="rounded border border-slate-200 px-3 py-1 font-semibold text-slate-600 hover:bg-slate-50"
          >
            {advancedOpen ? "Gelismis filtreleri gizle" : "Gelismis filtreler"}
          </button>
          <button
            type="button"
            onClick={onResetFilters}
            className="rounded border border-red-200 px-3 py-1 font-semibold text-red-600 hover:bg-red-50"
          >
            Filtreleri sifirla
          </button>
        </div>
      </div>
      {advancedOpen && (
        <div className="grid gap-3 md:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500" htmlFor="admin-user-status">
              Kullanici durumu
            </label>
            <select
              id="admin-user-status"
              value={statusValue}
              onChange={(event) => onStatusChange(event.target.value as StatusFilter)}
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
            >
              {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((key) => (
                <option key={key} value={key}>
                  {STATUS_LABELS[key]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500" htmlFor="admin-user-min-balance">
              Minimum Z-Token
            </label>
            <input
              id="admin-user-min-balance"
              type="text"
              value={minBalanceValue}
              onChange={(event) => onMinBalanceChange(event.target.value)}
              placeholder="Orn. 500"
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500" htmlFor="admin-user-min-level">
              Minimum seviye
            </label>
            <input
              id="admin-user-min-level"
              type="text"
              value={minLevelValue}
              onChange={(event) => onMinLevelChange(event.target.value)}
              placeholder="Orn. 10"
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function UserListPanel({
  users,
  loading,
  error,
  selectedUserId,
  onSelectUser,
  page,
  totalPages,
  onPageChange,
  totalCount,
  clientFilterActive,
  segmentLabel,
  canViewBalances,
}: {
  users: AdminUserSummary[];
  loading: boolean;
  error: string | null;
  selectedUserId: string | null;
  onSelectUser: (id: string) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalCount: number;
  clientFilterActive: boolean;
  segmentLabel: string;
  canViewBalances: boolean;
}) {
  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-gray-700">Kullanicilar</div>
          <div className="text-[11px] text-gray-500">Aktif segment: {segmentLabel}</div>
        </div>
        <div className="text-xs text-gray-500">
          Gosterilen: {formatNumber(users.length)} / {formatNumber(totalCount)}
          {clientFilterActive && <span className="ml-1 text-blue-600">• Filtre uygulanmis</span>}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center p-4 text-sm text-gray-500">Kullanicilar yukleniyor...</div>
      ) : error ? (
        <div className="flex flex-1 items-center justify-center p-4 text-sm text-red-500">{error}</div>
      ) : users.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-4 text-sm text-gray-500">
          {clientFilterActive
            ? "Secili filtrelere uyan kullanici bu sayfada bulunmuyor."
            : "Filtrelere uygun kullanici bulunamadi."}
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2">Kullanici</th>
                <th className="px-4 py-2">Roller</th>
                <th className="px-4 py-2 text-center">Aktivite</th>
                {canViewBalances && <th className="px-4 py-2 text-right">Bakiye</th>}
                <th className="px-4 py-2 text-center">Durum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => {
                const isSelected = user.id === selectedUserId;
                const otherRoles =
                  user.roles && user.roles.length > 0
                    ? user.roles.filter((role) => role !== user.role)
                    : [];
                const hasAvatar = hasCustomAvatar(user.avatarUrl);
                const initials = getInitials(user.username ?? user.email ?? "U");
                return (
                  <tr
                    key={user.id}
                    className={`cursor-pointer transition hover:bg-blue-50 ${isSelected ? "bg-blue-50" : ""}`}
                    onClick={() => onSelectUser(user.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 overflow-hidden rounded-full bg-slate-100">
                          {hasAvatar ? (
                            <img src={user.avatarUrl ?? ""} alt={user.username} className="h-full w-full object-cover" />
                          ) : (
                            <span className="flex h-full w-full items-center justify-center bg-gradient-to-br from-brand-500 to-slate-500 text-xs font-semibold text-white">
                              {initials}
                            </span>
                          )}
                        </div>
                        <div>
                          <div
                            className="font-semibold text-gray-800"
                            style={user.nameColor ? { color: user.nameColor } : undefined}
                          >
                            {user.username}
                          </div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="font-medium text-gray-800">{user.role ?? "-"}</div>
                      {otherRoles.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-gray-500">
                          {otherRoles.map((role) => (
                            <span key={role} className="rounded-full bg-slate-100 px-2 py-0.5">
                              {role}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">
                      <div className="text-sm font-semibold">Lv {user.level ?? "-"}</div>
                      <div className="text-[11px] text-gray-500">
                        {formatNumber(user.totalReads ?? 0)} okuma · {formatNumber(user.totalPurchases ?? 0)} satin alma
                      </div>
                    </td>
                    {canViewBalances && (
                      <td className="px-4 py-3 text-right font-medium text-gray-800">{formatNumber(user.zTokenBalance)}</td>
                    )}
                    <td className="px-4 py-3 text-center text-xs text-gray-600">
                      {user.isBannedAll ? (
                        <StatusBadge tone="danger" label="Platform yasakli" />
                      ) : user.isCommentBanned ? (
                        <StatusBadge tone="warning" label="Yorum yasakli" />
                      ) : (
                        <span className="text-gray-400">Aktif</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 0 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
      )}
    </div>
  );
}

function UserDetailPanel({
  detail,
  loading,
  error,
  editMode,
  onStartEdit,
  onCancelEdit,
  editForm,
  onEditChange,
  onRoleToggle,
  onSave,
  saveLoading,
  saveError,
  deleteLoading,
  onDeleteUser,
  onDeleteUserPackage,
  packageDeleteId,
  roleOptions,
  onBanToggle,
  showComments,
  onToggleComments,
  comments,
  commentLoading,
  commentError,
  commentPage,
  commentTotalPages,
  onCommentPageChange,
  onCommentAction,
  commentActionId,
  canViewBalances,
  canViewComments,
  canModerateComments,
  canDeleteComments,
  canEditProfile,
  canBanAll,
  canBanComments,
  canSeeSensitive,
  canAdjustZToken,
  canAssignRole,
  canViewProgress,
  canCriticalUserActions,
  canGiftActions,
  packageOptions,
  giftLoading,
  onResendEmailVerification,
  emailResendLoading,
  emailResendMessage,
  onGiftZToken,
  onGiftPackage,
}: {
  detail: AdminUserDetail | null;
  loading: boolean;
  error: string | null;
  editMode: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  editForm: EditFormState | null;
  onEditChange: (key: keyof EditFormState, value: string | boolean) => void;
  onRoleToggle: (role: string, checked: boolean) => void;
  onSave: () => void;
  saveLoading: boolean;
  saveError: string | null;
  deleteLoading: boolean;
  onDeleteUser: (mode: "soft" | "hard") => void;
  onDeleteUserPackage: (userPackageId: string, mode: "soft" | "hard") => void;
  packageDeleteId: string | null;
  roleOptions: string[];
  onBanToggle: (field: "isBannedAll" | "isCommentBanned", value: boolean) => void;
  showComments: boolean;
  onToggleComments: () => void;
  comments: AdminCommentItem[];
  commentLoading: boolean;
  commentError: string | null;
  commentPage: number;
  commentTotalPages: number;
  onCommentPageChange: (page: number) => void;
  onCommentAction: (action: "hide" | "unhide" | "delete", id: string) => void;
  commentActionId: string | null;
  canViewBalances: boolean;
  canViewComments: boolean;
  canModerateComments: boolean;
  canDeleteComments: boolean;
  canEditProfile: boolean;
  canBanAll: boolean;
  canBanComments: boolean;
  canSeeSensitive: boolean;
  canAdjustZToken: boolean;
  canAssignRole: boolean;
  canViewProgress: boolean;
  canCriticalUserActions: boolean;
  canGiftActions: boolean;
  packageOptions: AdminPackageItem[];
  giftLoading: "ztoken" | "package" | null;
  onResendEmailVerification: () => void;
  emailResendLoading: boolean;
  emailResendMessage: string | null;
  onGiftZToken: (amount: number, note?: string) => Promise<boolean>;
  onGiftPackage: (packageId: string, multiplier: number, note?: string) => Promise<boolean>;
}) {
  const roleSelectOptions = useMemo(() => {
    const unique = new Set<string>();
    roleOptions.forEach((role) => {
      const trimmed = role.trim();
      if (trimmed) {
        unique.add(trimmed);
      }
    });
    const activeRoles =
      editMode && editForm
        ? editForm.roles ?? []
        : detail?.roles && detail.roles.length > 0
          ? detail.roles
          : detail?.role
            ? [detail.role]
            : [];
    activeRoles.forEach((role) => {
      const trimmed = role.trim();
      if (trimmed) {
        unique.add(trimmed);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [roleOptions, editMode, editForm, detail?.roles, detail?.role]);

  const roleSummary =
    detail && detail.roles && detail.roles.length > 0
      ? detail.roles.join(", ")
      : detail?.role ?? "-";

  type ProfileField = { label: string; value: string | number | null | undefined };
  const profileFields: ProfileField[] = detail
    ? ([
        { label: "E-posta", value: detail.email },
        { label: "E-posta dogrulama", value: detail.isEmailVerified ? "Dogrulandi" : "Dogrulanmadi" },
        { label: "Roller", value: roleSummary },
        canSeeSensitive ? { label: "Telefon", value: detail.phone } : null,
        canViewBalances ? { label: "Z-Token", value: formatNumber(detail.zTokenBalance) } : null,
        {
          label: "Çizer günlük limit",
          value:
            detail.creatorDailyPartLimit && detail.creatorDailyPartLimit > 0
              ? `${formatNumber(detail.creatorDailyPartLimit)} bölüm`
              : "Varsayılan",
        },
        {
          label: "İzlenme metriğine dahil",
          value:detail.excludeFromViewMetrics ? "Hayır (hariç)" : "Evet",
        },
        { label: "Isim rengi", value: detail.nameColor },
        { label: "Avatar cercevesi", value: detail.avatarFrame },
        { label: "Avatar adresi", value: detail.avatarUrl },
        { label: "Arka plan adresi", value: detail.backgroundUrl },
        { label: "Kayit tarihi", value: formatDateTime(detail.createdAt) },
      ] as Array<ProfileField | null>).filter((item): item is ProfileField => item !== null)
    : [];

  const [giftZAmount, setGiftZAmount] = useState("");
  const [giftZNote, setGiftZNote] = useState("");
  const [giftPackageId, setGiftPackageId] = useState("");
  const [giftPackageMultiplier, setGiftPackageMultiplier] = useState("1");
  const [giftPackageNote, setGiftPackageNote] = useState("");
  const [financeFilter, setFinanceFilter] = useState<FinanceTimelineFilter>("all");

  useEffect(() => {
    setGiftZAmount("");
    setGiftZNote("");
    setGiftPackageId(packageOptions[0]?.id ?? "");
    setGiftPackageMultiplier("1");
    setGiftPackageNote("");
  }, [detail?.id, packageOptions]);

  useEffect(() => {
    setFinanceFilter("all");
  }, [detail?.id]);

  const financeTimeline = useMemo<UserFinanceTimelineItem[]>(() => {
    if (!detail) {
      return [];
    }

    const transactions: UserFinanceTimelineItem[] = detail.recentTransactions.map((transaction) => ({
      id: `tx-${transaction.id}`,
      kind: "transaction",
      title: transaction.type,
      occurredAt: transaction.createdAt,
      amountZ: transaction.amount,
      source: transaction.source ?? transaction.type,
      reason: transaction.reason ?? transaction.description ?? null,
    }));

    const purchases: UserFinanceTimelineItem[] = detail.tokenPurchases.map((purchase) => ({
      id: `purchase-${purchase.id}`,
      kind: "purchase",
      title: `${formatNumber(purchase.tokenAmount)} Z token satin alim`,
      occurredAt: purchase.createdAtUtc,
      amountZ: purchase.tokenAmount,
      amountFiat: `${formatNumber(purchase.priceAmount)} ${purchase.currency}`,
      source: purchase.source ?? "token_purchase",
      reason: purchase.reason ?? null,
      status: purchase.status,
    }));

    const unlocks: UserFinanceTimelineItem[] = detail.recentUnlocks.map((unlock) => ({
      id: `unlock-${unlock.id}`,
      kind: "unlock",
      title: unlock.partTitle || "Bolum acma",
      occurredAt: unlock.purchasedAt,
      amountZ: unlock.price ? unlock.price * -1 : 0,
      source: unlock.source ?? "single_unlock",
      reason: unlock.reason ?? null,
    }));

    return [...transactions, ...purchases, ...unlocks].sort(
      (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    );
  }, [detail]);

  const filteredFinanceTimeline = useMemo(() => {
    if (financeFilter === "all") {
      return financeTimeline;
    }
    return financeTimeline.filter((item) => item.kind === financeFilter);
  }, [financeTimeline, financeFilter]);

  const financeTypeCounts = useMemo(() => {
    const initial: Record<FinanceTimelineFilter, number> = {
      all: financeTimeline.length,
      transaction: 0,
      purchase: 0,
      unlock: 0,
    };
    financeTimeline.forEach((item) => {
      initial[item.kind] += 1;
    });
    return initial;
  }, [financeTimeline]);

  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">Kullanici detayi</div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-sm text-gray-500">Kullanici detaylari yukleniyor...</div>
        ) : error ? (
          <div className="p-4 text-sm text-red-500">{error}</div>
        ) : !detail ? (
          <div className="p-4 text-sm text-gray-500">Detay gormek icin listeden kullanici secin.</div>
        ) : (
          <div className="space-y-4 p-4 text-sm">
            <section className="space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3
                    className="text-base font-semibold text-gray-800"
                    style={detail.nameColor ? { color: detail.nameColor } : undefined}
                  >
                    {detail.username}
                  </h3>
                  <div className="text-xs text-gray-500">{detail.email}</div>
                  {detail.phone && <div className="text-xs text-gray-500">Telefon: {detail.phone}</div>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canBanAll && (
                    <ActionButton
                      tone={detail.isBannedAll ? "danger" : "outline-danger"}
                      onClick={() => onBanToggle("isBannedAll", !detail.isBannedAll)}
                      disabled={!canBanAll}
                    >
                      {detail.isBannedAll ? "Platform yasagini kaldir" : "Platformdan yasakla"}
                    </ActionButton>
                  )}
                  {canBanComments && (
                    <ActionButton
                      tone={detail.isCommentBanned ? "warning" : "outline-warning"}
                      onClick={() => onBanToggle("isCommentBanned", !detail.isCommentBanned)}
                      disabled={!canBanComments}
                    >
                      {detail.isCommentBanned ? "Yorum yasagini kaldir" : "Yorumlari yasakla"}
                    </ActionButton>
                  )}
                  {canViewComments && (
                    <ActionButton tone="outline-primary" onClick={onToggleComments} disabled={!canViewComments}>
                      {showComments ? "Yorumlari gizle" : "Yorumlari goster"}
                    </ActionButton>
                  )}
                  {!detail.isEmailVerified && (
                    <ActionButton
                      tone="outline-primary"
                      onClick={onResendEmailVerification}
                      disabled={emailResendLoading}
                    >
                      {emailResendLoading ? "Mail gonderiliyor..." : "Dogrulama maili gonder"}
                    </ActionButton>
                  )}
                  {canCriticalUserActions && (
                    <>
                      <ActionButton tone="outline-warning" onClick={() => onDeleteUser("soft")} disabled={deleteLoading}>
                        {deleteLoading ? "Isleniyor..." : "Soft Sil"}
                      </ActionButton>
                      <ActionButton tone="outline-danger" onClick={() => onDeleteUser("hard")} disabled={deleteLoading}>
                        {deleteLoading ? "Isleniyor..." : "Hard Sil"}
                      </ActionButton>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {!editMode ? (
                  <ActionButton tone="primary" onClick={onStartEdit} disabled={!canEditProfile}>
                    Profili duzenle
                  </ActionButton>
                ) : (
                  <>
                    <ActionButton tone="primary" onClick={onSave} disabled={saveLoading || !canEditProfile}>
                      {saveLoading ? "Kaydediliyor..." : "Kaydet"}
                    </ActionButton>
                    <ActionButton tone="secondary" onClick={onCancelEdit} disabled={saveLoading}>
                      Iptal
                    </ActionButton>
                  </>
                )}
              </div>
              {saveError && <div className="text-xs text-red-500">{saveError}</div>}
              {emailResendMessage && <div className="text-xs text-blue-600">{emailResendMessage}</div>}
            </section>

            {editMode && editForm ? (
              <SectionCard title="Profil ayarlari">
                <div className="grid gap-3 md:grid-cols-2">
                  <LabeledInput label="Isim" value={editForm.name} onChange={(v) => onEditChange("name", v)} />
                  <LabeledInput label="E-posta" value={editForm.email} onChange={(v) => onEditChange("email", v)} />
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={editForm.isEmailVerified}
                      onChange={(event) => onEditChange("isEmailVerified", event.target.checked)}
                    />
                    E-posta dogrulandi
                  </label>
                  {canSeeSensitive && (
                    <LabeledInput label="Telefon" value={editForm.phone} onChange={(v) => onEditChange("phone", v)} />
                  )}
                  {canAdjustZToken && (
                    <LabeledInput
                      label="Z-Token bakiyesi"
                      value={editForm.zTokenBalance}
                      onChange={(v) => onEditChange("zTokenBalance", v)}
                    />
                  )}
                  <LabeledInput
                    label="Çizer günlük limit"
                    placeholder="Boş bırakılırsa varsayılan"
                    value={editForm.creatorDailyPartLimit}
                    onChange={(v) => onEditChange("creatorDailyPartLimit", v)}
                  />
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={editForm.excludeFromViewMetrics}
                      onChange={(event) => onEditChange("excludeFromViewMetrics", event.target.checked)}
                    />
                    Bu kullanıcıyı view istatistiklerinden hariç tut
                  </label>
                  {canAssignRole && (
                    <div className="flex flex-col gap-1 text-xs text-gray-600">
                      <span>Roller</span>
                      <div className="flex flex-wrap gap-2">
                        {roleSelectOptions.length === 0 ? (
                          <span className="text-gray-400">Tanımlı rol bulunamadı.</span>
                        ) : (
                          roleSelectOptions.map((role) => {
                            const checked = editForm.roles.includes(role);
                            return (
                              <label
                                key={role}
                                className={`flex items-center gap-2 rounded border px-2 py-1 text-[11px] ${
                                  checked ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-300 text-gray-600"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  className="h-3 w-3 accent-blue-600"
                                  checked={checked}
                                  onChange={(event) => onRoleToggle(role, event.target.checked)}
                                />
                                {role}
                              </label>
                            );
                          })
                        )}
                      </div>
                      <span className="text-[11px] text-gray-500">En az bir rol seçili olmalı.</span>
                    </div>
                  )}
                  <LabeledInput
                    label="Isim rengi (hex)"
                    value={editForm.nameColor}
                    onChange={(v) => onEditChange("nameColor", v)}
                    placeholder="#000000"
                  />
                  <LabeledInput
                    label="Avatar cercevesi"
                    placeholder="Bos birakilirsa sifirlanir"
                    value={editForm.avatarFrame}
                    onChange={(v) => onEditChange("avatarFrame", v)}
                  />
                  <LabeledInput label="Avatar adresi" value={editForm.avatarUrl} onChange={(v) => onEditChange("avatarUrl", v)} />
                  <LabeledInput
                    label="Arka plan adresi"
                    value={editForm.backgroundUrl}
                    onChange={(v) => onEditChange("backgroundUrl", v)}
                  />
                </div>
              </SectionCard>
            ) : (
              <SectionCard title="Profil bilgileri">
                <div className="grid gap-2 text-xs text-gray-600 md:grid-cols-2">
                  {profileFields.map((field) => <InfoRow key={field.label} label={field.label} value={field.value} />)}
                </div>
              </SectionCard>
            )}

            {canViewProgress && (
              <SectionCard title="Ilerleme">
                {detail.progress ? (
                  <div className="space-y-2 text-xs text-gray-700">
                    <div className="flex items-center justify-between">
                      <span>Seviye {formatNumber(detail.progress.level)}</span>
                      <span>
                        {formatNumber(detail.progress.currentExp)} / {formatNumber(detail.progress.nextLevelExp)} XP
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded bg-gray-200">
                      <div
                        className="h-full rounded bg-blue-500"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.round((detail.progress.currentExp / Math.max(detail.progress.nextLevelExp, 1)) * 100),
                          )}%`,
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <StatTile label="Okunan bolum" value={detail.progress.totalReads} />
                      <StatTile label="Yorum" value={detail.progress.totalComments} />
                      <StatTile label="Satin alma" value={detail.progress.totalPurchases} />
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">Ilerleme bilgisi yok.</div>
                )}
              </SectionCard>
            )}

            <SectionCard title="Rozetler">
              {detail.achievements.length === 0 ? (
                <div className="text-xs text-gray-500">Henuz rozet kazanilmamis.</div>
              ) : (
                <div className="grid grid-cols-1 gap-2 text-xs text-gray-700 md:grid-cols-2">
                  {detail.achievements.map((achievement) => (
                    <div key={achievement.id} className="flex items-center gap-2 rounded border border-gray-200 bg-white p-2">
                      {achievement.iconUrl ? (
                        <img src={achievement.iconUrl} alt={achievement.title} className="h-8 w-8 rounded object-cover" />
                      ) : (
                        <span className="flex h-8 w-8 items-center justify-center rounded bg-blue-100 text-blue-500">*</span>
                      )}
                      <div>
                        <div className="font-medium text-gray-800">{achievement.title}</div>
                        <div className="text-[10px] text-gray-500">{formatDateTime(achievement.earnedAt)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard title="Aktif paketler">
              {detail.activePackages.length === 0 ? (
                <div className="text-xs text-gray-500">Aktif paket yok.</div>
              ) : (
                <div className="space-y-2 text-xs text-gray-700">
                  {detail.activePackages.map((pkg) => (
                    <div key={pkg.id} className="rounded border border-gray-200 bg-white p-2">
                      <div className="font-semibold text-gray-800">{pkg.packageName}</div>
                      <div className="flex flex-wrap gap-2 text-[11px] text-gray-600">
                        <span>Gunluk premium: {pkg.dailyQuotaPremium ?? "-"}</span>
                        <span>Gunluk free: {pkg.dailyQuotaFree ?? "-"}</span>
                        <span>Sure: {pkg.durationDays ?? "-"} gun</span>
                        <span>
                          {formatDate(pkg.startDateUtc)} - {formatDate(pkg.endDateUtc)}
                        </span>
                      </div>
                      {canCriticalUserActions && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          <ActionButton
                            tone="outline-warning"
                            onClick={() => onDeleteUserPackage(pkg.id, "soft")}
                            disabled={packageDeleteId === pkg.id}
                          >
                            {packageDeleteId === pkg.id ? "Isleniyor..." : "Paketi Soft Sil"}
                          </ActionButton>
                          <ActionButton
                            tone="outline-danger"
                            onClick={() => onDeleteUserPackage(pkg.id, "hard")}
                            disabled={packageDeleteId === pkg.id}
                          >
                            {packageDeleteId === pkg.id ? "Isleniyor..." : "Paketi Hard Sil"}
                          </ActionButton>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard title="Okuma geçmişi">
              {detail.recentReadLogs.length === 0 ? (
                <div className="text-xs text-gray-500">Son okuma kaydı bulunmuyor.</div>
              ) : (
                <HistoryList
                  items={detail.recentReadLogs.map((readLog) => ({
                    id: readLog.id,
                    title: `${readLog.contentTitle ?? "İçerik"} / ${readLog.partTitle ?? "Bölüm"}`,
                    subtitle: `${readLog.quotaSource ?? "unknown"} · ${formatDateTime(readLog.readAtUtc)}`,
                  }))}
                />
              )}
            </SectionCard>

            {canViewBalances && (
              <SectionCard title="Z-Token satın alımları">
                {detail.tokenPurchases.length === 0 ? (
                  <div className="text-xs text-gray-500">Token satın alım kaydı bulunmuyor.</div>
                ) : (
                  <HistoryList
                    items={detail.tokenPurchases.map((purchase) => ({
                      id: purchase.id,
                      title: `${formatNumber(purchase.tokenAmount)} Z · ${formatNumber(purchase.priceAmount)} ${purchase.currency}`,
                      subtitle: `${purchase.status} · ${purchase.source ?? "token_purchase"} · ${purchase.reason ?? "-"} · ${formatDateTime(purchase.createdAtUtc)}`,
                    }))}
                  />
                )}
              </SectionCard>
            )}

            {canViewBalances && (
              <SectionCard title="Z-Token hareket özeti">
                {detail.transactionSummary ? (
                  <div className="space-y-3 text-xs text-gray-700">
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                      <StatTile label="Toplam giriş" value={`${formatNumber(detail.transactionSummary.totalIncoming)} Z`} />
                      <StatTile label="Toplam çıkış" value={`${formatNumber(detail.transactionSummary.totalOutgoing)} Z`} />
                      <StatTile label="Hediye giriş" value={`${formatNumber(detail.transactionSummary.giftIncoming)} Z`} />
                      <StatTile label="Hareket" value={detail.transactionSummary.movementCount} />
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                      <InfoRow label="Paket harcaması" value={`${formatNumber(detail.transactionSummary.packageSpend)} Z`} />
                      <InfoRow label="Tek bölüm harcaması" value={`${formatNumber(detail.transactionSummary.singleUnlockSpend)} Z`} />
                      <InfoRow label="Satın alım yüklemesi" value={`${formatNumber(detail.transactionSummary.tokenPurchaseLoad)} Z`} />
                    </div>
                    <div className="text-[11px] text-gray-500">
                      Son hareket: {detail.transactionSummary.lastActivityAt ? formatDateTime(detail.transactionSummary.lastActivityAt) : "-"}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">Token hareket özeti bulunmuyor.</div>
                )}
              </SectionCard>
            )}

            {canViewBalances && (
              <SectionCard title="Finans timeline">
                <div className="space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-gray-600">
                      Toplam: {formatNumber(financeTypeCounts.all)} hareket
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <label className="text-gray-600" htmlFor="finance-timeline-filter">
                        Filtre
                      </label>
                      <select
                        id="finance-timeline-filter"
                        value={financeFilter}
                        onChange={(event) => setFinanceFilter(event.target.value as FinanceTimelineFilter)}
                        className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                      >
                        <option value="all">Tum ({formatNumber(financeTypeCounts.all)})</option>
                        <option value="transaction">Islem ({formatNumber(financeTypeCounts.transaction)})</option>
                        <option value="purchase">Satin alim ({formatNumber(financeTypeCounts.purchase)})</option>
                        <option value="unlock">Bolum acma ({formatNumber(financeTypeCounts.unlock)})</option>
                      </select>
                    </div>
                  </div>
                  {filteredFinanceTimeline.length === 0 ? (
                    <div className="text-xs text-gray-500">Filtreye uygun finans hareketi bulunmuyor.</div>
                  ) : (
                    <ul className="space-y-2">
                      {filteredFinanceTimeline.map((item) => {
                        const amount = item.amountZ ?? null;
                        const amountLabel =
                          amount === null
                            ? "-"
                            : `${amount >= 0 ? "+" : ""}${formatNumber(amount)} Z`;
                        const amountClass =
                          amount === null ? "text-gray-500" : amount >= 0 ? "text-emerald-600" : "text-rose-600";
                        const kindLabel =
                          item.kind === "transaction"
                            ? "Islem"
                            : item.kind === "purchase"
                              ? "Satin alim"
                              : "Bolum acma";
                        return (
                          <li key={item.id} className="rounded border border-gray-200 bg-white p-2 text-xs">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="font-semibold text-gray-800">{item.title}</div>
                              <div className={`font-semibold ${amountClass}`}>{amountLabel}</div>
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700">{kindLabel}</span>
                              <span>{formatDateTime(item.occurredAt)}</span>
                              {item.amountFiat ? <span>{item.amountFiat}</span> : null}
                              {item.status ? <span>Durum: {item.status}</span> : null}
                              <span>Kaynak: {item.source ?? "-"}</span>
                              <span>Neden: {item.reason ?? "-"}</span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </SectionCard>
            )}

            {canViewBalances && (
              <SectionCard title="Harcama kırılımı">
                {detail.transactionBreakdown.length === 0 ? (
                  <div className="text-xs text-gray-500">Kırılım kaydı bulunmuyor.</div>
                ) : (
                  <div className="space-y-2 text-xs">
                    {detail.transactionBreakdown.map((entry) => (
                      <div key={entry.type} className="rounded border border-gray-200 bg-white p-2 text-gray-700">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="font-semibold text-gray-800">{entry.type}</div>
                          <div className="text-[11px] text-gray-500">{entry.movementCount} hareket</div>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-gray-600">
                          <span>Giriş: {formatNumber(entry.totalIncoming)} Z</span>
                          <span>Çıkış: {formatNumber(entry.totalOutgoing)} Z</span>
                          <span>Son: {entry.lastActivityAt ? formatDateTime(entry.lastActivityAt) : "-"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            )}

            {canGiftActions && (
              <SectionCard title="Hediye işlemleri (Admin)">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2 rounded border border-slate-200 bg-white p-3">
                    <div className="text-xs font-semibold text-slate-700">Z-Token hediye et</div>
                    <LabeledInput
                      label="Miktar"
                      type="number"
                      value={giftZAmount}
                      onChange={(v) => setGiftZAmount(v)}
                    />
                    <LabeledInput
                      label="Not (opsiyonel)"
                      value={giftZNote}
                      onChange={(v) => setGiftZNote(v)}
                    />
                    <ActionButton
                      tone="primary"
                      onClick={() => {
                        const amount = Number(giftZAmount);
                        if (!Number.isFinite(amount) || amount <= 0) {
                          void notifyDialog("Geçerli bir Z-Token miktarı girin.", "error");
                          return;
                        }
                        void onGiftZToken(amount, giftZNote || undefined).then((ok) => {
                          if (ok) {
                            setGiftZAmount("");
                            setGiftZNote("");
                          }
                        });
                      }}
                      disabled={giftLoading !== null}
                    >
                      {giftLoading === "ztoken" ? "Gönderiliyor..." : "Z-Token gönder"}
                    </ActionButton>
                  </div>

                  <div className="space-y-2 rounded border border-slate-200 bg-white p-3">
                    <div className="text-xs font-semibold text-slate-700">Paket hediye et</div>
                    <label className="flex flex-col gap-1 text-xs text-gray-600">
                      <span>Paket</span>
                      <select
                        value={giftPackageId}
                        onChange={(event) => setGiftPackageId(event.target.value)}
                        className="rounded border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="">Paket seçin</option>
                        {packageOptions.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name} ({pkg.durationDays} gün)
                          </option>
                        ))}
                      </select>
                    </label>
                    <LabeledInput
                      label="Süre çarpanı"
                      type="number"
                      value={giftPackageMultiplier}
                      onChange={(v) => setGiftPackageMultiplier(v)}
                    />
                    <LabeledInput
                      label="Not (opsiyonel)"
                      value={giftPackageNote}
                      onChange={(v) => setGiftPackageNote(v)}
                    />
                    <ActionButton
                      tone="primary"
                      onClick={() => {
                        if (!giftPackageId) {
                          void notifyDialog("Önce bir paket seçin.", "error");
                          return;
                        }
                        const multiplier = Number(giftPackageMultiplier);
                        if (!Number.isFinite(multiplier) || multiplier <= 0) {
                          void notifyDialog("Geçerli bir süre çarpanı girin.", "error");
                          return;
                        }
                        void onGiftPackage(giftPackageId, multiplier, giftPackageNote || undefined).then((ok) => {
                          if (ok) {
                            setGiftPackageNote("");
                            setGiftPackageMultiplier("1");
                          }
                        });
                      }}
                      disabled={giftLoading !== null}
                    >
                      {giftLoading === "package" ? "Gönderiliyor..." : "Paket gönder"}
                    </ActionButton>
                  </div>
                </div>
              </SectionCard>
            )}

            <SectionCard title="Paket gecmisi">
              {detail.packageHistory.length === 0 ? (
                <div className="text-xs text-gray-500">Gecmis paket bilgisi yok.</div>
              ) : (
                <HistoryList
                  items={detail.packageHistory.map((pkg) => ({
                    id: pkg.id,
                    title: pkg.packageName,
                    subtitle: `${formatDate(pkg.startDateUtc)} - ${formatDate(pkg.endDateUtc)}`,
                  }))}
                />
              )}
            </SectionCard>

            <SectionCard title="Son acilan bolumler">
              {detail.recentUnlocks.length === 0 ? (
                <div className="text-xs text-gray-500">Premium bolum acilmis degil.</div>
              ) : (
                <HistoryList
                  items={detail.recentUnlocks.map((unlock) => ({
                    id: unlock.id,
                    title: unlock.partTitle,
                    subtitle: `${formatNumber(unlock.price)} Z · ${formatDate(unlock.purchasedAt)}`,
                  }))}
                />
              )}
            </SectionCard>

            {canViewBalances && (
              <SectionCard title="Son islemler">
                {detail.recentTransactions.length === 0 ? (
                  <div className="text-xs text-gray-500">Islem kaydi bulunmuyor.</div>
                ) : (
                  <HistoryList
                    items={detail.recentTransactions.map((transaction) => ({
                      id: transaction.id,
                      title: `${transaction.type} · ${formatNumber(transaction.amount)} Z`,
                      subtitle: `${transaction.source ?? transaction.type} · ${transaction.reason ?? transaction.description ?? "-"} · ${formatDateTime(transaction.createdAt)}`,
                    }))}
                  />
                )}
              </SectionCard>
            )}

            {showComments && canViewComments && (
              <CommentSection
                comments={comments}
                loading={commentLoading}
                error={commentError}
                page={commentPage}
                totalPages={commentTotalPages}
                onPageChange={onCommentPageChange}
                onAction={onCommentAction}
                busyId={commentActionId}
                canModerateComments={canModerateComments}
                canDeleteComments={canDeleteComments}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CommentSection({
  comments,
  loading,
  error,
  page,
  totalPages,
  onPageChange,
  onAction,
  busyId,
  canModerateComments,
  canDeleteComments,
}: {
  comments: AdminCommentItem[];
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onAction: (action: "hide" | "unhide" | "delete", id: string) => void;
  busyId: string | null;
  canModerateComments: boolean;
  canDeleteComments: boolean;
}) {
  return (
    <SectionCard title="Kullanici yorumlari">
      {loading ? (
        <div className="text-xs text-gray-500">Yorumlar yukleniyor...</div>
      ) : error ? (
        <div className="text-xs text-red-500">{error}</div>
      ) : comments.length === 0 ? (
        <div className="text-xs text-gray-500">Yorum bulunmuyor.</div>
      ) : (
        <div className="space-y-2">
          {comments.map((comment) => (
            <div key={comment.id} className="space-y-2 rounded border border-gray-200 bg-white p-3 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-gray-800">{comment.contentTitle ?? "Icerik"}</div>
                  <div className="text-[11px] text-gray-500">
                    {comment.contentPartTitle ?? "Bolum"} · {formatDateTime(comment.createdAt)}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <ActionButton
                    tone={comment.isHidden ? "success" : "warning"}
                    onClick={() => onAction(comment.isHidden ? "unhide" : "hide", comment.id)}
                    disabled={busyId === comment.id || !canModerateComments}
                  >
                    {comment.isHidden ? "Gorunur yap" : "Gizle"}
                  </ActionButton>
                  <ActionButton
                    tone="danger"
                    onClick={() => onAction("delete", comment.id)}
                    disabled={busyId === comment.id || !canDeleteComments}
                  >
                    Sil
                  </ActionButton>
                </div>
              </div>
              <p className="whitespace-pre-wrap text-sm text-gray-700">{comment.text}</p>
            </div>
          ))}
          {totalPages > 0 && <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />}
        </div>
      )}
    </SectionCard>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <h4 className="text-xs font-semibold uppercase text-gray-500">{title}</h4>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1 text-xs text-gray-600">
      <span>{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
      />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  let display: string;
  if (typeof value === "number") {
    display = formatNumber(value);
  } else if (value === null || value === undefined || value === "") {
    display = "-";
  } else {
    display = String(value);
  }

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase text-gray-500">{label}</span>
      <span className="break-words text-sm text-gray-700">{display}</span>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number | string | null | undefined }) {
  const display =
    typeof value === "number"
      ? formatNumber(value)
      : value === null || value === undefined || value === ""
      ? "-"
      : String(value);

  return (
    <div className="rounded border border-gray-200 bg-white p-2">
      <div className="text-[11px] uppercase text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-800">{display}</div>
    </div>
  );
}

function HistoryList({ items }: { items: { id: string; title: string; subtitle: string }[] }) {
  return (
    <ul className="space-y-1 text-xs text-gray-700">
      {items.map((item) => (
        <li key={item.id} className="flex flex-col gap-1 rounded border border-gray-100 bg-white px-2 py-2 md:flex-row md:items-center md:justify-between">
          <span className="font-medium text-gray-800">{item.title}</span>
          <span className="text-gray-500 md:text-right">{item.subtitle}</span>
        </li>
      ))}
    </ul>
  );
}

function StatusBadge({ tone, label }: { tone: "danger" | "warning"; label: string }) {
  const styles = tone === "danger" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700";
  return <span className={`inline-flex rounded px-2 py-1 font-semibold ${styles}`}>{label}</span>;
}

function ActionButton({
  tone,
  onClick,
  disabled,
  children,
}: {
  tone: "primary" | "secondary" | "outline-primary" | "outline-warning" | "outline-danger" | "danger" | "warning" | "success";
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const base = "rounded px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60";
  const toneClass: Record<typeof tone, string> = {
    primary: "bg-blue-600 text-white hover:bg-blue-700",
    secondary: "border border-gray-300 text-gray-600 hover:bg-gray-100",
    "outline-primary": "border border-purple-600 text-purple-600 hover:bg-purple-50",
    "outline-warning": "border border-amber-600 text-amber-600 hover:bg-amber-50",
    "outline-danger": "border border-red-600 text-red-600 hover:bg-red-50",
    danger: "bg-red-600 text-white hover:bg-red-700",
    warning: "bg-amber-500 text-white hover:bg-amber-600",
    success: "bg-emerald-600 text-white hover:bg-emerald-700",
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} ${toneClass[tone]}`}>
      {children}
    </button>
  );
}

function Pagination({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (page: number) => void }) {
  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-xs text-gray-600">
      <div>
        Sayfa {formatNumber(Math.min(page + 1, totalPages))} / {formatNumber(totalPages)}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          className="rounded border border-gray-300 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Onceki
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page + 1 >= totalPages}
          className="rounded border border-gray-300 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Sonraki
        </button>
      </div>
    </div>
  );
}

function buildEditForm(detail: AdminUserDetail): EditFormState {
  const rawRoles =
    detail.roles && detail.roles.length > 0
      ? detail.roles
      : detail.role
        ? [detail.role]
        : ["Üye"];
  const normalizedRoles = rawRoles
    .map((role) => role.trim())
    .filter((role) => role.length > 0);
  return {
    name: detail.username ?? "",
    email: detail.email ?? "",
    isEmailVerified: Boolean(detail.isEmailVerified),
    phone: detail.phone ?? "",
    roles: normalizedRoles.length > 0 ? normalizedRoles : ["Üye"],
    zTokenBalance: detail.zTokenBalance.toString(),
    nameColor: detail.nameColor ?? "",
    avatarFrame: detail.avatarFrame ?? "",
    avatarUrl: detail.avatarUrl ?? "",
    backgroundUrl: detail.backgroundUrl ?? "",
    creatorDailyPartLimit:detail.creatorDailyPartLimit ? String(detail.creatorDailyPartLimit) : "",
    excludeFromViewMetrics: Boolean(detail.excludeFromViewMetrics),
  };
}

function parseFilterValue(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const normalized = Number(trimmed.replace(/,/g, "."));
  return Number.isFinite(normalized) ? normalized : null;
}

function parseNumber(value: string, fallback: number): number {
  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }
  const normalized = Number(trimmed.replace(/,/g, "."));
  return Number.isFinite(normalized) ? normalized : fallback;
}

function parseOptionalPositiveInt(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const normalized = Number(trimmed.replace(/,/g, "."));
  if (!Number.isFinite(normalized)) {
    return null;
  }
  const asInt = Math.trunc(normalized);
  return asInt > 0 ? asInt : 0;
}

function formatNumber(value: number | string): string {
  if (typeof value === "string") {
    const maybe = Number(value);
    if (Number.isFinite(maybe)) {
      return new Intl.NumberFormat("tr-TR").format(maybe);
    }
    return value;
  }
  return new Intl.NumberFormat("tr-TR").format(value);
}

function formatDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleDateString("tr-TR");
}

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) {
    return "-";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString("tr-TR");
}
